<script setup>
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ErrorState from '~/common/components/ErrorState.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import { numberParam, parseQueryParams, textParam } from '~/common/QueryParams';

import DashboardChart from './dashboard-page/DashboardChart.vue';
import InsightsDetailTable from './insights-page/InsightsDetailTable.vue';
import InsightsFilterCard from './insights-page/InsightsFilterCard.vue';

const route = useRoute();
const router = useRouter();

const filterCard = ref(null);

const chartData = ref([]);
const detailData = ref({ records: [], metadata: {} });
const chartError = ref(null);
const detailError = ref(null);
const busy = ref(false);
const detailBusy = ref(false);

const detailParams = {
  page: numberParam(1),
  sortBy: textParam('merged_date'),
  sortDirection: textParam('desc'),
};

const initialValues = parseQueryParams(route.query, detailParams);

const detailPage = ref(initialValues.page);
const detailSortBy = ref(initialValues.sortBy);
const detailSortDirection = ref(initialValues.sortDirection);

const allSeries = computed(() => {
  if (chartData.value.length === 0) {
    return [];
  }

  const keys = [...new Set(chartData.value.flatMap(row => Object.keys(row)))]
    .filter(k => k !== 'activity_date');

  return keys.filter(key =>
    chartData.value.some(row => row[key] > 0),
  );
});

const seriesLabels = computed(() => {
  const hierarchy = filterCard.value?.hierarchyData || {};
  const labels = {};
  allSeries.value.forEach((key) => {
    labels[key] = hierarchy[key]?.name
      || key.replace(/^(group|section|devops)::/, '');
  });

  return labels;
});

const graphDates = computed(() => chartData.value.map(d => d.activity_date).filter(Boolean));

const graphPoints = computed(() => {
  if (!graphDates.value.length || !allSeries.value.length) {
    return [];
  }

  return graphDates.value.map((date) => {
    const row = chartData.value.find(d => d.activity_date === date);

    return Object.fromEntries(
      allSeries.value.map(series => [series, Number(row?.[series]) || 0]),
    );
  });
});

const chartReady = computed(() =>
  chartData.value.length > 0
  && allSeries.value.length > 0
  && graphDates.value.length > 0
  && graphPoints.value.length > 0,
);

const currentFilterParams = ref({});

const fetchChartData = async () => {
  busy.value = true;
  chartError.value = null;
  try {
    const response = await axios.get('/api/v1/insights', { params: currentFilterParams.value });
    if (response.status !== 200) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }

    chartData.value = response.data;
  } catch (e) {
    chartError.value = e;
  }

  busy.value = false;
};

const fetchDetailData = async () => {
  detailBusy.value = true;
  detailError.value = null;
  try {
    const response = await axios.get('/api/v1/insights/detail', {
      params: {
        ...currentFilterParams.value,
        page: detailPage.value,
        page_size: 20,
        sort_by: detailSortBy.value,
        sort_direction: detailSortDirection.value,
      },
    });
    if (response.status !== 200) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }

    detailData.value = response.data;
  } catch (e) {
    detailError.value = e;
  }

  detailBusy.value = false;
};

const updateQueryString = (filterQuery = {}) => {
  const query = {
    ...filterQuery,
    page: detailParams.page.serialize(detailPage.value),
    sortBy: detailParams.sortBy.serialize(detailSortBy.value),
    sortDirection: detailParams.sortDirection.serialize(detailSortDirection.value),
  };

  router.replace({ query });
};

const onFilter = ({ params, query }) => {
  currentFilterParams.value = params;
  detailPage.value = 1;
  updateQueryString(query);
  fetchChartData();
  fetchDetailData();
};

const onDetailSort = ({ sortBy, sortDirection }) => {
  detailSortBy.value = sortBy;
  detailSortDirection.value = sortDirection;
  updateQueryString(filterCard.value?.buildQueryString?.() || {});
  fetchDetailData();
};

watch(detailPage, () => {
  updateQueryString(filterCard.value?.buildQueryString?.() || {});
  fetchDetailData();
});
</script>

<template>
  <div>
    <h2 class="my-3">
      Insights
    </h2>

    <InsightsFilterCard
      ref="filterCard"
      :initial-filters="route.query"
      @filter="onFilter"
    />

    <ErrorState v-if="chartError" />
    <ThemedOverlay
      v-else
      :show="busy"
    >
      <DashboardChart
        v-if="chartReady"
        :graph-points="graphPoints"
        :graph-dates="graphDates"
        :chart-type="filterCard?.chartType || 'line'"
        :stacked="true"
        :all-series="allSeries"
        :series-labels="seriesLabels"
      />
      <p
        v-else-if="!busy"
        class="text-center text-muted my-5"
      >
        No data found for the selected filters.
      </p>
    </ThemedOverlay>

    <h3 class="my-3">
      Detail
    </h3>

    <ErrorState v-if="detailError" />
    <InsightsDetailTable
      v-else
      v-model:page="detailPage"
      :records="detailData.records"
      :metadata="detailData.metadata"
      :busy="detailBusy"
      :initial-sort-by="detailSortBy"
      :initial-sort-direction="detailSortDirection"
      @sort="onDetailSort"
    />
  </div>
</template>
